"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { Building, TrendingUp, Crown, Video } from "lucide-react";

interface ProductSectionProps {
  onScrollToPricing: () => void;
}

export function ProductSection({ onScrollToPricing }: ProductSectionProps) {
  const t = useTranslations("Home.product");
  const titleAnimation = useScrollAnimation({ threshold: 0.2 });
  const methodAnimation = useScrollAnimation({ threshold: 0.2 });
  const structureAnimation = useScrollAnimation({ threshold: 0.2 });
  const qualityAnimation = useScrollAnimation({ threshold: 0.2 });

  return (
    <section className="py-24 px-4 sm:px-8 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-900/50 to-black"></div>
      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Título Principal */}
        <div
          ref={titleAnimation.ref}
          className={`text-center mb-16 transition-all duration-1000 ease-out ${
            titleAnimation.isVisible
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-8"
          }`}
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 font-[family-name:var(--font-big-shoulders)] uppercase leading-tight">
            {t("title")}
          </h2>
          <p className="text-xl sm:text-2xl text-gray-300 max-w-3xl mx-auto">
            {t("subtitle")}
          </p>
        </div>

        {/* Bloque Principal: La Visión del Método */}
        <div
          ref={methodAnimation.ref}
          className={`grid lg:grid-cols-2 gap-12 items-center mb-20 transition-all duration-1000 ease-out ${
            methodAnimation.isVisible
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-8"
          }`}
        >
          {/* Imagen/Gráfico */}
          <div className="relative">
            <div className="aspect-[3/4] sm:aspect-[4/5] lg:aspect-[1/1] relative overflow-hidden rounded-lg">
              <Image
                src="/images/escalera-y-ellos-de-verde.webp"
                alt={t("method.imageAlt")}
                fill
                className="object-contain"
                sizes="(max-width: 768px) 100vw, 50vw"
              />
            </div>
          </div>

          {/* Contenido */}
          <div className="space-y-6">
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight font-[family-name:var(--font-big-shoulders)] uppercase">
              {t("method.title")}
            </h3>
            <p className="text-base sm:text-lg text-gray-300 leading-relaxed">
              {t("method.description")}
            </p>
          </div>
        </div>

        {/* Estructura del Curso - 48 Clases */}
        <div
          ref={structureAnimation.ref}
          className={`mb-20 transition-all duration-1000 ease-out ${
            structureAnimation.isVisible
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-8"
          }`}
        >
          <div className="text-center mb-12">
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 font-[family-name:var(--font-big-shoulders)] uppercase">
              {t("structure.title")}
            </h3>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              {t("structure.description")}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Fundamentos */}
            <div className="text-center space-y-4 p-6 bg-gray-900/30 rounded-lg border border-white/10">
              <div className="w-16 h-16 mx-auto flex items-center justify-center bg-[#E91E63]/20 rounded-full">
                <Building className="w-8 h-8 text-[#E91E63]" />
              </div>
              <h4 className="text-xl font-bold text-white font-[family-name:var(--font-big-shoulders)] uppercase">
                {t("structure.fundamentals.title")}
              </h4>
              <p className="text-sm text-gray-400 mb-2">
                {t("structure.fundamentals.classes")}
              </p>
              <p className="text-gray-300 text-sm leading-relaxed">
                {t("structure.fundamentals.description")}
              </p>
            </div>

            {/* Intermedio */}
            <div className="text-center space-y-4 p-6 bg-gray-900/30 rounded-lg border border-white/10">
              <div className="w-16 h-16 mx-auto flex items-center justify-center bg-[#E91E63]/20 rounded-full">
                <TrendingUp className="w-8 h-8 text-[#E91E63]" />
              </div>
              <h4 className="text-xl font-bold text-white font-[family-name:var(--font-big-shoulders)] uppercase">
                {t("structure.intermediate.title")}
              </h4>
              <p className="text-sm text-gray-400 mb-2">
                {t("structure.intermediate.classes")}
              </p>
              <p className="text-gray-300 text-sm leading-relaxed">
                {t("structure.intermediate.description")}
              </p>
            </div>

            {/* Avanzado */}
            <div className="text-center space-y-4 p-6 bg-gray-900/30 rounded-lg border border-white/10">
              <div className="w-16 h-16 mx-auto flex items-center justify-center bg-[#E91E63]/20 rounded-full">
                <Crown className="w-8 h-8 text-[#E91E63]" />
              </div>
              <h4 className="text-xl font-bold text-white font-[family-name:var(--font-big-shoulders)] uppercase">
                {t("structure.advanced.title")}
              </h4>
              <p className="text-sm text-gray-400 mb-2">
                {t("structure.advanced.classes")}
              </p>
              <p className="text-gray-300 text-sm leading-relaxed">
                {t("structure.advanced.description")}
              </p>
            </div>
          </div>
        </div>

        {/* Calidad de Producción */}
        <div
          ref={qualityAnimation.ref}
          className={`grid lg:grid-cols-2 gap-12 items-center transition-all duration-1000 ease-out ${
            qualityAnimation.isVisible
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-8"
          }`}
        >
          {/* Contenido */}
          <div className="space-y-6">
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight font-[family-name:var(--font-big-shoulders)] uppercase">
              {t("quality.title")}
            </h3>
            <p className="text-base sm:text-lg text-gray-300 leading-relaxed">
              {t("quality.description")}
            </p>

            {/* Features de calidad */}
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="flex items-center space-x-3">
                <Video className="w-5 h-5 text-[#E91E63]" />
                <span className="text-sm text-gray-300">
                  {t("quality.features.video")}
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <Video className="w-5 h-5 text-[#E91E63]" />
                <span className="text-sm text-gray-300">
                  {t("quality.features.angles")}
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <Video className="w-5 h-5 text-[#E91E63]" />
                <span className="text-sm text-gray-300">
                  {t("quality.features.audio")}
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <Video className="w-5 h-5 text-[#E91E63]" />
                <span className="text-sm text-gray-300">
                  {t("quality.features.editing")}
                </span>
              </div>
            </div>

            {/* CTA implícito */}
            <div className="pt-4">
              <button
                onClick={onScrollToPricing}
                className="text-[#E91E63] hover:text-[#D81B60] font-semibold transition-colors"
              >
                {t("quality.cta")} →
              </button>
            </div>
          </div>

          {/* Imagen de calidad */}
          <div className="relative">
            <div className="aspect-[4/3] relative overflow-hidden rounded-lg">
              <Image
                src="/images/produccion-pro.webp"
                alt={t("quality.imageAlt")}
                fill
                className="object-contain"
                sizes="(max-width: 768px) 100vw, 50vw"
              />
            </div>
            {/* Badge 4K */}
            <div className="absolute top-4 right-4 bg-[#E91E63] text-white px-3 py-1 rounded-full text-sm font-bold">
              4K
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
